/**
 * WorkReportCancelDialog — the ONE cancellation confirmation + API call
 * for work reports (SAFE-DELETE1).
 *
 * Replaces WorkReportDeleteDialog: a report is a proof of worked time,
 * so it is CANCELLED with a mandatory reason and preserved — the UI must
 * not promise "löschen" when the system stornos. Every list keeps
 * mounting exactly one of these, so there is still a single CRUD path.
 *
 * The reason field gates the confirm button (min 3 chars after trim —
 * the backend enforces the same bounds and the DB CHECK backs both).
 * Who and when are server-side; this dialog sends only the reason.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { cancelAdminWorkReport } from "../../../../api/adminData";

export const CANCEL_REASON_MIN = 3;
export const CANCEL_REASON_MAX = 500;

export default function WorkReportCancelDialog({
  report, // the report pending cancellation, or null when closed
  onCancel,
  onCancelled,
}) {
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);

  // A fresh target starts with a fresh reason — a text left over from
  // the previously cancelled report must never be submitted for the
  // next one.
  useEffect(() => {
    setReason("");
  }, [report?.id]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < CANCEL_REASON_MIN;
  const tooLong = trimmed.length > CANCEL_REASON_MAX;
  const canSubmit = Boolean(report) && !pending && !tooShort && !tooLong;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setPending(true);
    try {
      await cancelAdminWorkReport(report.id, trimmed);
      toast.success("Rapport storniert.");
      onCancelled?.();
    } catch (err) {
      toast.error(err?.message || "Rapport konnte nicht storniert werden.");
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog
      open={Boolean(report)}
      onOpenChange={(open) => {
        if (!open && !pending) onCancel?.();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rapport stornieren?</AlertDialogTitle>
          <AlertDialogDescription>
            {report
              ? `„${report.title}" wird storniert: Der Rapport bleibt ` +
                "als Nachweis erhalten, zählt aber nicht mehr in " +
                "Arbeitszeit- und Auswertungssummen. Diese Aktion kann " +
                "nicht rückgängig gemacht werden."
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-1.5">
          <label
            htmlFor="wr-cancel-reason"
            className="text-[12px] font-medium text-muted-foreground"
          >
            Stornogrund (erforderlich)
          </label>
          <textarea
            id="wr-cancel-reason"
            rows={3}
            maxLength={CANCEL_REASON_MAX + 1}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder="z. B. Rapport versehentlich doppelt erfasst."
            className="w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          {tooShort ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Mindestens {CANCEL_REASON_MIN} Zeichen.
            </p>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
          {/* Not AlertDialogAction: that primitive closes on click even
              when the submit must be blocked by the empty reason. A
              plain button keeps the dialog open until the API resolves. */}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? "Storniert …" : "Rapport stornieren"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
